import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2,
  User, Building2, Handshake, Layers } from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";
import PageHero, { ScheduleConsultationButton } from "@/components/site/PageHero";

const ICONS = { User, Building2, Handshake, Layers };

export default function Solutions() {
  return (
    <div data-testid="solutions-page">
      <PageHero
        eyebrow="Our Solutions"
        title="Financial systems built for clarity, compliance, and growth."
        subtitle="From individual tax preparation to executive-level oversight for nonprofits and ministries — we offer integrated solutions across four core service lines."
        image={SITE.assets.officeModern}
      >
        <ScheduleConsultationButton testid="solutions-hero-schedule-btn" />
        <Link to="/executive-financial-command" className="trj-btn-outline-white" data-testid="solutions-hero-efc-btn">
          Executive Financial Command™ <ArrowRight className="w-4 h-4" />
        </Link>
      </PageHero>

      {/* SERVICES DETAIL */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-20">
          {SERVICES.map((s, i) => {
            const Ico = ICONS[s.icon] || User;
            const reversed = i % 2 === 1;
            return (
              <article
                key={s.slug}
                id={s.slug}
                data-testid={`solution-section-${s.slug}`}
                className="grid lg:grid-cols-12 gap-10 items-center scroll-mt-28"
              >
                <div className={`lg:col-span-7 ${reversed ? "lg:order-2" : ""}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 bg-[var(--trj-navy)] text-[var(--trj-gold)] flex items-center justify-center">
                      <Ico className="w-5 h-5" />
                    </div>
                    <div className="trj-eyebrow">0{i + 1} · {s.title}</div>
                  </div>
                  <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">{s.tagline}</h2>
                  <div className="trj-divider-gold mt-5" />
                  <p className="mt-6 text-[var(--trj-muted)] leading-relaxed max-w-2xl">{s.description}</p>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-2 max-w-xl">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-sm text-[var(--trj-navy)]">
                        <CheckCircle2 className="w-4 h-4 text-[var(--trj-green)]" /> {it}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex gap-3 flex-wrap">
                    <ScheduleConsultationButton testid={`${s.slug}-schedule-btn`} label="Schedule Consultation" />
                    <Link to="/contact" className="trj-btn-outline-navy text-sm" data-testid={`${s.slug}-contact-btn`}>
                      Ask a Question <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
                <div className={`lg:col-span-5 ${reversed ? "lg:order-1" : ""}`}>
                  <div className="relative">
                    <img
                      src={[SITE.assets.boardroom, SITE.assets.team, SITE.assets.ministry, SITE.assets.officeModern][i % 4]}
                      alt={s.title}
                      className="w-full h-[380px] object-cover"
                    />
                    <div className="absolute bottom-0 left-0 bg-[var(--trj-navy)] text-white px-4 py-2 text-[10px] uppercase tracking-[0.25em]">
                      {s.title}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--trj-light)] py-20 text-center" data-testid="solutions-cta">
        <div className="max-w-3xl mx-auto px-6">
          <div className="trj-eyebrow mb-4">Not sure where to start?</div>
          <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">
            A short conversation can change everything.
          </h2>
          <p className="mt-5 text-[var(--trj-muted)] leading-relaxed">
            Book a consultation and we&apos;ll help you identify the right starting point.
          </p>
          <div className="mt-8 flex justify-center flex-wrap gap-3">
            <ScheduleConsultationButton testid="solutions-cta-schedule-btn" />
            <Link to="/executive-financial-command" className="trj-btn-outline-navy" data-testid="solutions-cta-efc-btn">
              Executive Financial Command™
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
